import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Clapperboard, Crop, Palette, Plus, Snowflake, Type } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { fmt, formatBytes, pluralize } from '../format'
import { useClipMutations } from '../hooks/useClipMutations'
import { useClipPlayback, type VideoMetadata } from '../hooks/useClipPlayback'
import { useColorGrades } from '../hooks/useColorGrades'
import { useProjectExport } from '../hooks/useProjectExport'
import { useProxyReconcile } from '../hooks/useProxyReconcile'
import { useTextOverlays } from '../hooks/useTextOverlays'
import {
  clipLabel,
  cropPresetRatio,
  DEFAULT_CROP_PRESET,
  FULL_FRAME,
  gradeFilterAt,
  textsAt,
  videoById,
  type CropRect,
  type ProjectVideo,
  type ToolProject,
} from '../model'
import {
  buildTimeline,
  clipBounds,
  freezeBounds,
  videoOffset,
  type TimelineMode,
} from '../timeline'
import { AddVideoDialog } from './AddVideoDialog'
import { ClipInspector } from './ClipInspector'
import { ClipsPanel } from './ClipsPanel'
import { CropInspector } from './CropInspector'
import { CropOverlay, fitRatio } from './CropOverlay'
import { DurationProbes } from './DurationProbes'
import { GradeInspector } from './GradeInspector'
import { InlineNameEdit } from './InlineNameEdit'
import { TextLayer } from './TextLayer'
import { TextInspector } from './TextInspector'
import { Timeline, type Armed } from './Timeline'
import { VideoStage } from './VideoStage'
import shell from '../styles/shell.module.scss'
import styles from '../styles/editor.module.scss'

/** What the timeline lays out. ``clips`` is the "excluir as partes não
 * selecionadas" view: same player, same axis, only the marked ranges — so
 * it plays as one continuous video rather than as a playlist. Nothing is
 * deleted; switching back brings the full footage into view. */
const MODE_OPTIONS: ReadonlyArray<SegmentedOption<TimelineMode>> = [
  { value: 'full', label: 'Vídeos completos' },
  { value: 'clips', label: 'Só os cortes' },
]

export interface ProjectEditorProps {
  project: ToolProject
  onChange: (updater: (project: ToolProject) => ToolProject) => void
  onClose: () => void
}

export function ProjectEditor({ project, onChange, onClose }: ProjectEditorProps) {
  const [armed, setArmed] = useState<Armed | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [mode, setMode] = useState<TimelineMode>('full')
  /** One selection across the whole tag strip: a caption and a grade are
   * both a bar on the same axis, and two independent selections would let
   * two inspectors claim the panel at once. */
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null)
  const [selectedClipId, setSelectedClipId] = useState<number | null>(null)
  const [cropEditing, setCropEditing] = useState(false)
  /** The preset's key, not its ratio: "Original" and "16:9" resolve to the
   * same number on landscape footage, and the chips have to show which one
   * was actually chosen. */
  const [cropPreset, setCropPreset] = useState(DEFAULT_CROP_PRESET)

  // "Só os cortes" with nothing marked would be a zero-length timeline and
  // a dead player, so the mode falls back until there's something to show.
  const effectiveMode: TimelineMode =
    mode === 'clips' && project.clips.some((c) => !c.hidden) ? 'clips' : 'full'
  const timeline = useMemo(() => buildTimeline(project, effectiveMode), [project, effectiveMode])

  // Captions, grades and freezes belong to the **cut**, not to the raw
  // footage: they are part of the video that gets exported, and that video
  // is the clip sequence. So they're stored on the clips axis and only
  // exist in "só os cortes" — there is no meaningful place for them over
  // material the cut discards.
  //
  // The cost, accepted: trimming a clip ripples every later effect, the
  // way it does in any NLE.
  const effectsActive = effectiveMode === 'clips'

  // Clip bars, placed on the timebase, with their held stretches. In
  // ``full`` mode a clip is a mark inside its video's stretch; in ``clips``
  // mode the clip *is* one or more stretches.
  const timelineClips = useMemo(
    () =>
      project.clips
        .map((clip, index) => {
          const bounds = clipBounds(timeline, clip)
          return bounds
            ? {
                clip,
                index,
                start: bounds.start,
                end: bounds.end,
                freezes: freezeBounds(timeline, clip),
              }
            : null
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    [project.clips, timeline],
  )

  useProxyReconcile(onChange)

  /**
   * Cache what the browser learned about a source the server never probed.
   *
   * Dimensions are only adopted when there are none: what the player loads
   * is the *proxy*, whose shape matches the master but whose size doesn't,
   * so overwriting a real probe with 720p would be a downgrade. Adopting
   * them when the alternative is nothing is not — the stage needs an aspect
   * or the frame collapses, and captions measured against a wrong-shaped
   * box land somewhere the export won't put them.
   */
  const rememberMetadata = useCallback(
    (videoId: string, info: VideoMetadata) => {
      onChange((p) => {
        const known = p.videos.find((v) => v.id === videoId)
        if (!known) return p
        const newDuration = Math.abs(known.duration - info.duration) >= 0.05
        const newSize = (!known.width || !known.height) && info.width > 0 && info.height > 0
        if (!newDuration && !newSize) return p
        return {
          ...p,
          videos: p.videos.map((v) =>
            v.id === videoId
              ? {
                  ...v,
                  duration: newDuration ? info.duration : v.duration,
                  ...(newSize ? { width: info.width, height: info.height } : {}),
                }
              : v,
          ),
        }
      })
    },
    [onChange],
  )

  const playback = useClipPlayback({
    timeline,
    videos: project.videos,
    onMetadata: rememberMetadata,
  })
  // Destructured so callbacks below depend on the stable handlers rather
  // than the object literal the hook returns every render — otherwise the
  // timeline's drag listeners re-subscribe on every mousemove.
  const { playhead, playing, bound, seekTo, pause: pausePlayback } = playback
  const duration = timeline.duration
  const currentSegment = timeline.segments[playback.segmentIndex] ?? null

  // The video under the playhead — what the header describes and what a
  // newly marked clip belongs to.
  const activeVideoId = currentSegment?.videoId ?? project.videos[0]?.id ?? ''
  const activeVideo = videoById(project, activeVideoId) ?? project.videos[0] ?? null

  // --- Mutations -----------------------------------------------------------
  const clips = useClipMutations({ onChange, timeline, videos: project.videos })

  const texts = useTextOverlays({
    onChange,
    texts: project.texts,
    timeline,
    playhead,
    seekTo,
    pause: pausePlayback,
    onSelect: setSelectedTagId,
  })

  const grades = useColorGrades({
    onChange,
    grades: project.grades,
    timeline,
    playhead,
    seekTo,
    pause: pausePlayback,
    onSelect: setSelectedTagId,
  })

  const selectedText = project.texts.find((t) => t.id === selectedTagId) ?? null
  const selectedGrade = project.grades.find((g) => g.id === selectedTagId) ?? null
  const selectedClip = project.clips.find((c) => c.id === selectedClipId) ?? null
  const selectedClipIndex = selectedClip ? project.clips.indexOf(selectedClip) : -1

  /** Captions on screen right now — the layer only draws these. */
  const visibleTexts = useMemo(
    () => (effectsActive ? textsAt(project.texts, playhead) : []),
    [project.texts, playhead, effectsActive],
  )

  /** The grades covering this instant, as one CSS ``filter``. The very same
   * string the export reproduces filter-for-filter. */
  const videoFilter = useMemo(
    () => (effectsActive ? gradeFilterAt(project.grades, playhead) : 'none'),
    [project.grades, playhead, effectsActive],
  )

  // Captions and grades as timeline tags, on one strip. A caption's label
  // is its own text, so a stack of them is readable without selecting each.
  const tags = useMemo(() => {
    if (!effectsActive) return []
    return [
      ...project.texts.map((t) => ({
        id: t.id,
        start: t.start,
        end: t.end,
        label: t.text || '(vazio)',
        color: t.color,
        kind: 'text' as const,
      })),
      ...project.grades.map((g) => ({
        id: g.id,
        start: g.start,
        end: g.end,
        label: 'Cor',
        color: '#7DD3FC',
        kind: 'grade' as const,
      })),
    ]
  }, [project.texts, project.grades, effectsActive])

  /** Route a tag gesture to whichever list owns it. The strip is generic on
   * purpose; this is the one place that has to know the difference. */
  const isGrade = useCallback(
    (id: string) => project.grades.some((g) => g.id === id),
    [project.grades],
  )

  // --- Video mutations -----------------------------------------------------
  // Appends to the end of the timeline — that's what "concatenates" means
  // here, and the player runs straight into it without a mode switch.
  const addVideo = useCallback(
    (video: ProjectVideo) => {
      onChange((p) => ({ ...p, videos: [...p.videos, video] }))
    },
    [onChange],
  )

  const removeVideo = useCallback(
    (video: ProjectVideo) => {
      const clipCount = project.clips.filter((c) => c.videoId === video.id).length
      const suffix = clipCount > 0 ? ` e ${pluralize(clipCount, 'clipe', 'clipes')}` : ''
      if (!window.confirm(`Remover "${video.filename}"${suffix} deste projeto?`)) return
      onChange((p) => ({
        ...p,
        videos: p.videos.filter((v) => v.id !== video.id),
        clips: p.clips.filter((c) => c.videoId !== video.id),
      }))
    },
    [onChange, project.clips],
  )

  // --- Crop ----------------------------------------------------------------
  const frameAspect =
    activeVideo?.width && activeVideo.height ? activeVideo.width / activeVideo.height : 16 / 9
  const crop = project.crop

  const setCrop = useCallback(
    (next: CropRect | null) => {
      onChange((p) => ({ ...p, crop: next }))
    },
    [onChange],
  )

  const cropRatio = cropPresetRatio(cropPreset, frameAspect)

  const pickPreset = useCallback(
    (key: string) => {
      setCropPreset(key)
      const ratio = cropPresetRatio(key, frameAspect)
      // "Livre" only unlocks — re-fitting there would throw away a framing
      // the user had already placed. A locked ratio re-fits, because
      // applying a lock to whatever rectangle happens to be there would
      // either break the lock or crop something nobody asked for.
      // Fit-and-centre is where every crop tool starts.
      if (ratio !== null) setCrop(fitRatio(frameAspect, ratio))
    },
    [frameAspect, setCrop],
  )

  // --- Timeline interaction ------------------------------------------------
  const { addClip } = clips
  const pickTime = useCallback(
    (time: number) => {
      if (!armed) {
        seekTo(time)
        return
      }
      if (armed.phase === 'start') {
        setArmed({ phase: 'end', start: time })
        // Move the player to the mark so the user sees the frame they just
        // chose as the clip's first one.
        seekTo(time)
        return
      }
      let start = armed.start
      let end = time
      if (end < start) [start, end] = [end, start]
      if (end - start < MIN_MARK_S) end = Math.min(duration, start + MIN_MARK_S)
      addClip(start, end)
      setArmed(null)
      seekTo(start)
    },
    [armed, addClip, duration, seekTo],
  )

  const scrub = useCallback(
    (time: number) => {
      // keepBound: dragging a marker shouldn't cancel a running preview —
      // it's the same intent (look at this exact moment).
      seekTo(time, { keepBound: true })
    },
    [seekTo],
  )

  // --- Preview -------------------------------------------------------------
  // Playing "one clip" is playing the timeline with a stop at its end, so
  // the clip's bounds have to be expressed on the timebase first.
  const { playBounded, playFrom } = playback
  const playClip = useCallback(
    (index: number) => {
      const placed = timelineClips.find((entry) => entry.index === index)
      if (!placed) return
      playBounded({ start: placed.start, end: placed.end, clipId: placed.clip.id })
    },
    [playBounded, timelineClips],
  )

  /**
   * Changing the mode changes the *axis*, so the player has to be re-seated
   * on the new one — and second 0 of "só os cortes" is a completely
   * different frame from second 0 of the full footage.
   *
   * It has to wait a render: ``setMode`` doesn't rebuild ``timeline`` until
   * the next one, so seeking in the same tick would resolve the position
   * against the axis being left behind. That's what made the sequence
   * preview start at the top of the video instead of at the first clip.
   */
  const pendingModeSeek = useRef<{ at: number; play: boolean } | null>(null)
  useEffect(() => {
    const request = pendingModeSeek.current
    if (!request) return
    pendingModeSeek.current = null
    if (request.play) playFrom(request.at)
    else seekTo(request.at)
  }, [effectiveMode, playFrom, seekTo])

  const switchMode = useCallback((next: TimelineMode, options?: { play?: boolean }) => {
    pendingModeSeek.current = { at: 0, play: options?.play ?? false }
    setMode(next)
  }, [])

  /**
   * "Prévia da sequência" switches to ``clips`` and plays from the top.
   *
   * That *is* the sequence: the mode already drops everything unmarked (and
   * everything hidden), so playback runs the cuts back to back with no
   * jumping. It also means the preview and the "excluir partes não
   * selecionadas" view are the same thing seen from two entry points,
   * instead of two implementations that can disagree about the export.
   */
  const playSequence = useCallback(() => {
    if (!project.clips.some((c) => !c.hidden)) return
    // Already there: the effect above won't fire, so play directly.
    if (effectiveMode === 'clips') playFrom(0)
    else switchMode('clips', { play: true })
  }, [effectiveMode, playFrom, switchMode, project.clips])

  // --- Freeze --------------------------------------------------------------
  const { addFreezeAt } = clips
  const freezeHere = useCallback(() => {
    pausePlayback()
    const created = addFreezeAt(playhead)
    // Selecting the clip opens the inspector on the freeze that was just
    // dropped, which is where its length is adjusted.
    if (created) {
      const point = timeline.segments[playback.segmentIndex]
      if (point?.clipId) setSelectedClipId(point.clipId)
    }
  }, [addFreezeAt, pausePlayback, playhead, timeline.segments, playback.segmentIndex])

  const canFreeze = effectsActive && currentSegment?.clipId != null && !currentSegment.freezeId

  // --- Export --------------------------------------------------------------
  const { exportState, runExport } = useProjectExport({
    project,
    onChange,
    fallbackVideo: activeVideo,
  })

  // --- Derived -------------------------------------------------------------
  // Where each video begins, for the dividers the timeline draws between
  // concatenated sources.
  const joins = useMemo(() => timeline.segments.slice(1).map((s) => s.start), [timeline.segments])
  const videoNumberById = useMemo(
    () => Object.fromEntries(project.videos.map((v, i) => [v.id, i + 1])),
    [project.videos],
  )
  const clipCountByVideo = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const c of project.clips) counts[c.videoId] = (counts[c.videoId] ?? 0) + 1
    return counts
  }, [project.clips])

  if (!activeVideo) {
    // Every project is created with a video and the last one can't be
    // removed, so this is a corrupted-storage guard, not a real state.
    return (
      <div className={shell.page}>
        <p className={shell.error}>Este projeto está sem vídeos.</p>
        <Button variant="ghost" leadingIcon={<ArrowLeft />} onClick={onClose}>
          Voltar aos projetos
        </Button>
      </div>
    )
  }

  return (
    <div className={styles.pageEditor}>
      <div className={styles.projectBar}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={onClose}
          aria-label="Voltar aos projetos"
        >
          <ArrowLeft aria-hidden />
        </button>
        <div className={styles.tsIcon}>
          <Clapperboard aria-hidden />
        </div>
        <div className={styles.tsBody}>
          <div className={styles.tsName}>
            <InlineNameEdit
              value={project.name}
              ariaLabel="Renomear projeto"
              title="Clique para renomear o projeto"
              onCommit={(name) => {
                onChange((p) => ({ ...p, name }))
              }}
            />
          </div>
          <div className={styles.tsMeta}>
            {[
              pluralize(project.videos.length, 'vídeo', 'vídeos'),
              pluralize(project.clips.length, 'clipe', 'clipes'),
              activeVideo.width && activeVideo.height
                ? `${String(activeVideo.width)}×${String(activeVideo.height)}`
                : null,
              duration ? fmt(duration) : null,
              formatBytes(activeVideo.fileSize),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {project.clips.length > 0 ? (
          <Segmented<TimelineMode>
            options={MODE_OPTIONS}
            value={effectiveMode}
            // Both axes start at zero but mean different things, so the
            // player is re-seated once the new timeline exists.
            onValueChange={(next) => {
              switchMode(next)
            }}
            aria-label="O que a linha do tempo mostra"
          />
        ) : null}
        <Button
          variant="ghost"
          leadingIcon={<Type />}
          onClick={texts.addText}
          disabled={!effectsActive || duration <= 0}
          title={
            effectsActive
              ? 'Adicionar texto no ponto atual'
              : 'Textos vivem no corte final — mude para "Só os cortes"'
          }
        >
          Texto
        </Button>
        <Button
          variant="ghost"
          leadingIcon={<Palette />}
          onClick={grades.addGrade}
          disabled={!effectsActive || duration <= 0}
          title={
            effectsActive
              ? 'Colorir um trecho do corte'
              : 'Coloração vive no corte final — mude para "Só os cortes"'
          }
        >
          Cor
        </Button>
        <Button
          variant="ghost"
          leadingIcon={<Snowflake />}
          onClick={freezeHere}
          disabled={!canFreeze}
          title={
            canFreeze
              ? 'Segurar o frame atual, esticando o clipe'
              : effectsActive
                ? 'Leve o cursor até dentro de um clipe'
                : 'Congelar vive no corte final — mude para "Só os cortes"'
          }
        >
          Congelar
        </Button>
        <Button
          variant={cropEditing ? 'primary' : 'ghost'}
          leadingIcon={<Crop />}
          onClick={() => {
            setCropEditing((prev) => !prev)
            // Opening the tool with no crop yet starts from the whole frame,
            // so there is a rectangle to grab.
            if (!cropEditing && !crop) setCrop({ ...FULL_FRAME })
          }}
          title="Enquadramento do vídeo final"
        >
          Enquadrar
        </Button>
        <Button
          variant="ghost"
          leadingIcon={<Plus />}
          onClick={() => {
            setAddOpen(true)
          }}
        >
          Adicionar vídeo
        </Button>
      </div>

      <div className={styles.editor}>
        <div className={styles.stage}>
          <VideoStage
            video={activeVideo}
            slots={playback.slots}
            handlersFor={playback.handlersFor}
            videos={project.videos}
            clipCountByVideo={clipCountByVideo}
            // The tabs are now navigation on one axis, not a mode switch:
            // clicking a video jumps the playhead to where it begins.
            onSelectVideo={(videoId) => {
              const offset = videoOffset(timeline, videoId)
              if (offset !== null) seekTo(offset)
            }}
            onRemoveVideo={removeVideo}
            playing={playing}
            playhead={playhead}
            duration={duration}
            armed={armed}
            nextClipNumber={project.clips.length + 1}
            volume={playback.volume}
            muted={playback.muted}
            onVolumeChange={playback.setVolume}
            onToggleMute={playback.toggleMute}
            crop={crop}
            cropEditing={cropEditing}
            videoFilter={videoFilter}
            frozen={currentSegment?.freezeId != null}
            cropOverlay={
              <CropOverlay
                crop={crop ?? FULL_FRAME}
                frameAspect={frameAspect}
                ratio={cropRatio}
                onChange={setCrop}
              />
            }
            overlay={
              <TextLayer
                texts={visibleTexts}
                time={playhead}
                selectedId={selectedTagId}
                onSelect={setSelectedTagId}
                onMove={texts.moveText}
                onEditText={texts.editTextContent}
                // Grabbing a caption mid-playback would fight the player
                // and hide the frame you're judging the position against.
                // While framing they stay visible but inert — the drag
                // belongs to the crop handles underneath the cursor.
                interactive={!playing && !cropEditing}
              />
            }
            onTogglePlay={playback.togglePlay}
            onVideoClick={() => {
              if (cropEditing) return
              setSelectedTagId(null)
              playback.togglePlay()
            }}
            onSeekBy={playback.seekBy}
            onToggleArm={() => {
              setArmed((prev) => (prev ? null : { phase: 'start' }))
            }}
          />

          <Timeline
            duration={duration}
            playhead={playhead}
            clips={timelineClips}
            joins={joins}
            editable={effectiveMode === 'full'}
            armed={armed}
            onPickTime={pickTime}
            onScrub={scrub}
            onClipBounds={clips.updateBounds}
            isPlaying={playing}
            onPausePlayback={playback.pause}
            onResumePlayback={playback.resume}
            tags={tags}
            selectedTagId={selectedTagId}
            onSelectTag={(id) => {
              if (id && isGrade(id)) grades.selectGradeAndSeek(id)
              else texts.selectTextAndSeek(id)
            }}
            onTagChange={(id, start, end) => {
              if (isGrade(id)) grades.patchGrade(id, { start, end })
              else texts.patchText(id, { start, end })
            }}
            onDuplicateTag={(id) => {
              if (isGrade(id)) grades.duplicateGrade(id)
              else texts.duplicateText(id)
            }}
            onDeleteTag={(id) => {
              if (isGrade(id)) grades.deleteGrade(id)
              else texts.deleteText(id)
            }}
          />
        </div>

        <div className={styles.sidePanel}>
          {cropEditing ? (
            <CropInspector
              crop={crop}
              frameAspect={frameAspect}
              preset={cropPreset}
              onPickPreset={pickPreset}
              onClear={() => {
                setCrop(null)
                setCropPreset(DEFAULT_CROP_PRESET)
              }}
            />
          ) : null}
          {selectedText ? (
            <TextInspector
              text={selectedText}
              onPatch={(patch) => {
                texts.patchText(selectedText.id, patch)
              }}
              onDelete={() => {
                texts.deleteText(selectedText.id)
              }}
            />
          ) : null}
          {selectedGrade ? (
            <GradeInspector
              grade={selectedGrade}
              onPatch={(patch) => {
                grades.patchGrade(selectedGrade.id, patch)
              }}
              onDelete={() => {
                grades.deleteGrade(selectedGrade.id)
              }}
            />
          ) : null}
          {selectedClip ? (
            <ClipInspector
              clip={selectedClip}
              index={selectedClipIndex}
              label={clipLabel(selectedClip, selectedClipIndex)}
              onSetSpeed={(speed) => {
                clips.setSpeed(selectedClip.id, speed)
              }}
              onToggleHidden={() => {
                clips.toggleHidden(selectedClip.id)
              }}
              onFreezeDuration={(freezeId, value) => {
                clips.setFreezeDuration(selectedClip.id, freezeId, value)
              }}
              onDeleteFreeze={(freezeId) => {
                clips.deleteFreeze(selectedClip.id, freezeId)
              }}
            />
          ) : null}
          <ClipsPanel
            project={project}
            videoNumberById={videoNumberById}
            previewClipId={bound?.clipId ?? null}
            selectedClipId={selectedClipId}
            onSelectClip={setSelectedClipId}
            onToggleHidden={clips.toggleHidden}
            sequenceMode={effectiveMode === 'clips'}
            isPlaying={playing}
            onPlayClip={playClip}
            onPlaySequence={playSequence}
            // Stopping is "rewind", not "leave this view": it stays in
            // whatever the timeline is showing and returns to the top of
            // it. Kicking the user back to the full footage would undo a
            // mode they chose on purpose.
            onStopPreview={() => {
              playback.pause()
              playback.clearBound()
              if (effectiveMode === 'clips') seekTo(0)
            }}
            onRenameClip={clips.renameClip}
            onDeleteClip={(clipId) => {
              clips.deleteClip(clipId)
              setSelectedClipId((prev) => (prev === clipId ? null : prev))
            }}
            onReorder={clips.reorder}
            exportState={exportState}
            onExportClips={() => {
              runExport('download')
            }}
            onExportMerged={() => {
              runExport('merge')
            }}
          />
        </div>
      </div>

      <DurationProbes videos={project.videos} onMetadata={rememberMetadata} />

      <AddVideoDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        usedTempPaths={project.videos.map((v) => v.tempPath)}
        onVideo={addVideo}
      />
    </div>
  )
}

/** Shortest clip a marker gesture may produce. Mirrors ``MIN_CLIP_S``; kept
 * local because this is the *gesture's* floor, applied on the timebase
 * before the mutation converts to source seconds. */
const MIN_MARK_S = 1
